import React, { useState, useEffect, useRef } from 'react';
import { useOrg } from '../../../hooks/useOrg';
import { api } from '../../../lib/api';
import ReferralPreferencesCard from './ReferralPreferencesCard';

export default function ProfileTab() {
  const { currentOrg, currentRole, currentMembership, refetchMemberships } = useOrg();
  const [exportingAll, setExportingAll] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const exportAll = async () => {
    setExportingAll(true); setExportMsg('');
    try { const name = await api.exportPracticeData(currentOrg.id); setExportMsg(`Saved ${name}`); }
    catch (e) { setExportMsg(e.message || 'Export failed'); }
    finally { setExportingAll(false); }
  };
  const fileRef = useRef(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (currentOrg) {
      setForm({
        name: currentOrg.name || '',
        email: currentOrg.email || '',
        phone: currentOrg.phone || '',
        website: currentOrg.website || '',
        address_line1: currentOrg.address_line1 || '',
        address_line2: currentOrg.address_line2 || '',
        city: currentOrg.city || '',
        state: currentOrg.state || '',
        zip: currentOrg.zip || '',
        description: currentOrg.description || '',
        logo_url: currentOrg.logo_url || '',
      });
      setLogoPreview(currentOrg.logo_url || null);
    }
  }, [currentOrg]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      // For now store as data URL — in production we'd upload to storage
      update('logo_url', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await api.updateOrg(currentOrg.id, {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        website: form.website || null,
        address_line1: form.address_line1 || null,
        address_line2: form.address_line2 || null,
        city: form.city || null,
        state: form.state || null,
        zip: form.zip || null,
        description: form.description || null,
        logo_url: form.logo_url || null,
      });
      setSuccess('Profile saved.');
      refetchMemberships();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (!currentOrg) return null;

  const initials = currentOrg.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'OR';

  return (
    <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
      <form onSubmit={handleSave} className="space-y-5">
        {error && <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">{error}</div>}
        {success && <div className="px-4 py-3 rounded-[10px] bg-success-soft text-success text-[13px] font-medium">{success}</div>}

        {/* Logo */}
        <div className="flex items-center gap-5">
          <div
            onClick={() => fileRef.current?.click()}
            className="w-[72px] h-[72px] rounded-[16px] flex items-center justify-center cursor-pointer overflow-hidden border-2 border-dashed border-strong hover:border-accent-light transition-all group flex-shrink-0"
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full gradient-accent flex items-center justify-center text-[20px] font-bold text-white">
                {initials}
              </div>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors"
            >
              {logoPreview ? 'Change logo' : 'Upload logo'}
            </button>
            <p className="text-[11px] text-muted-c mt-0.5">Recommended: 256x256px, PNG or JPG</p>
            {logoPreview && (
              <button
                type="button"
                onClick={() => { setLogoPreview(null); update('logo_url', ''); }}
                className="text-[11px] font-medium text-danger hover:text-[#DC2626] transition-colors mt-1"
              >
                Remove logo
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} />
        </div>

        {/* Name + Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Organization Name *</label>
            <input type="text" className={inputClass} value={form.name || ''} onChange={e => update('name', e.target.value)} required />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email</label>
            <input type="email" className={inputClass} value={form.email || ''} onChange={e => update('email', e.target.value)} placeholder="office@example.com" />
          </div>
        </div>

        {/* Phone + Website */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Phone</label>
            <input type="tel" className={inputClass} value={form.phone || ''} onChange={e => update('phone', e.target.value)} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Website</label>
            <input type="url" className={inputClass} value={form.website || ''} onChange={e => update('website', e.target.value)} placeholder="https://" />
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-[12px] font-semibold text-secondary-c mb-2">Address</label>
          <input type="text" className={inputClass} value={form.address_line1 || ''} onChange={e => update('address_line1', e.target.value)} placeholder="Street address" />
        </div>
        <input type="text" className={inputClass} value={form.address_line2 || ''} onChange={e => update('address_line2', e.target.value)} placeholder="Suite, unit, etc." />

        <div className="grid grid-cols-3 gap-3.5">
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">City</label>
            <input type="text" className={inputClass} value={form.city || ''} onChange={e => update('city', e.target.value)} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">State</label>
            <input type="text" className={inputClass} value={form.state || ''} onChange={e => update('state', e.target.value)} maxLength={2} />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">ZIP</label>
            <input type="text" className={inputClass} value={form.zip || ''} onChange={e => update('zip', e.target.value)} />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-[12px] font-semibold text-secondary-c mb-2">About / Description</label>
          <textarea className={inputClass} rows={3} value={form.description || ''} onChange={e => update('description', e.target.value)} placeholder="Brief description of your practice..." />
        </div>

        <div className="flex justify-end pt-5 border-t border-theme">
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>

      {/* Medical practices: what the referral search says about this
          practice (lien acceptance, languages). Renders nothing for law
          firms or when the referrals feature is off. */}
      <ReferralPreferencesCard />

      {/* Your data, your copy (BAA §6/§9). Owners and admins only; works
          even after the subscription ends. */}
      {(['owner', 'admin'].includes(currentRole) || currentMembership?.is_primary_org) && (
        <div className="bg-elevated rounded-[14px] border border-theme p-5 mt-6">
          <div className="text-[13px] font-semibold text-primary-c">Export all practice data</div>
          <p className="text-[12px] text-muted-c mt-1 mb-3 max-w-[640px]">
            Downloads everything this organization holds in MediLink as one ZIP: a folder per patient with documents, signed forms, visits, referrals, cases and notes as PDFs and CSVs, plus staff, locations and appointment types. Large practices may take a few minutes. Every export is recorded.
          </p>
          <button
            type="button"
            onClick={exportAll}
            disabled={exportingAll}
            className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all disabled:opacity-60"
          >
            {exportingAll ? 'Preparing your export…' : 'Download full export (ZIP)'}
          </button>
          {exportMsg && <div className="text-[12px] text-muted-c mt-2">{exportMsg}</div>}
        </div>
      )}
    </div>
  );
}
